import { useMemo, useState } from 'react';
import { useGetProjectAccessDoorsQuery } from 'app/services/projectBoardApi';
import {
  AhuSummaryPane,
  buildAhuSummaryRows,
  useRefetchOnTabReentry,
  type IAhuSummaryComponent,
} from './ahuSummaryShared';

/**
 * AHU ACCESSORIES TAB (`#ahu_accessories`) — dev-sync F port of
 * `project/static/project/mvc/ahu_accessories/view/ahu_accessories.js` +
 * `tmpl/ahu_accessories.tmpl` + `collection/access_doors.js` (KA-5258, PR #798).
 *
 * Same shape as the AHU Components tab: a left rail of accessory types with a
 * summary table (only "Access Doors" today — ahu_accessories.js:23-28) and a
 * READ-ONLY project-wide table on the right, grouped by AHU. Columns:
 * AHU | SECTION | ROUGH OPENING W x H (in) | MOUNTING PLANE | SWING |
 * HINGE SIDE, fed by `GET /api/v1.0/ahu/access_doors/project/{project_id}` →
 * `{id, section_id, ahu, ahu_id, section, mounting_plane, swing, hinge_side,
 * width, height}` (`AHUAccessoryAccessDoorProjectSerializer`,
 * api/rest_ahu/access_door_serializers.py:77-103 — the endpoint pre-dates
 * KA-5258; PR #798 only added `swing`).
 */

/** ahu_accessories.js:26-28 — the sidebar registry. */
const COMPONENTS: readonly IAhuSummaryComponent[] = [
  { key: 'access_doors', label: 'Access Doors' },
];

/**
 * ahu_accessories.tmpl:19-24. The ROUGH OPENING header mixes cases via
 * `.ahu-summary__th-lowercase` spans because the `<th>` is uppercased in CSS.
 */
const HEAD = (
  <tr>
    <th className="ahu-summary__col-ahu">AHU</th>
    <th className="ahu-summary__col-name">SECTION</th>
    <th className="ahu-summary__col-right ahu-summary__col-rough ahu-summary__col-fixed-lg">
      ROUGH OPENING W <span className="ahu-summary__th-lowercase">x</span> H{' '}
      <span className="ahu-summary__th-lowercase">(in)</span>
    </th>
    <th className="ahu-summary__col-fixed-md">MOUNTING PLANE</th>
    <th className="ahu-summary__col-fixed">SWING</th>
    <th className="ahu-summary__col-fixed">HINGE SIDE</th>
  </tr>
);

/**
 * `formatRough` (ahu_accessories.js:58-67): `"24.00 x 72.00"`, blank when
 * NEITHER dimension is set.
 *
 * Quirks reproduced verbatim:
 *  - only the `!w && !h` case short-circuits, so a door with just a width
 *    renders `"24.00 x "` and one with just a height renders `" x 72.00"`;
 *  - `0` formats to `"0.00"`, which is TRUTHY as a string — so a 0 × 0 door
 *    renders `"0.00 x 0.00"`, not blank;
 *  - the guard is `=== null || === undefined || === ''`, so any other
 *    non-numeric value falls through to `Number(v).toFixed(2)` → `"NaN"`.
 */
export const formatRough = (
  width: string | number | null | undefined,
  height: string | number | null | undefined
): string => {
  const fmt = (v: string | number | null | undefined): string =>
    v === null || v === undefined || v === '' ? '' : Number(v).toFixed(2);
  const w = fmt(width);
  const h = fmt(height);
  if (!w && !h) return '';
  return `${w} x ${h}`;
};

const AhuAccessoriesTab = ({
  projectId,
  active,
}: {
  projectId: number;
  active: boolean;
}) => {
  const { data: doors, refetch } = useGetProjectAccessDoorsQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  useRefetchOnTabReentry(active, refetch, 'ahu_accessories');

  const [selected, setSelected] = useState<string>(COMPONENTS[0].key);

  const rows = useMemo(() => buildAhuSummaryRows(doors ?? []), [doors]);

  // Empty until the first fetch resolves (ahu_accessories.js:44-49).
  if (!doors) return null;

  return (
    <AhuSummaryPane
      formatId="ahu_accessories_format"
      sidebarHeader="AHU Accessories"
      components={COMPONENTS}
      selected={selected}
      onSelect={setSelected}
      head={HEAD}
      colSpan={6}
      emptyLabel="No Access Door"
      isEmpty={doors.length === 0}
    >
      {rows.map(({ firstInGroup, ahu, rowspan, item }) => (
        <tr
          key={item.id}
          className={firstInGroup ? 'ahu-summary__group-start' : ''}
        >
          {firstInGroup && (
            <td className="ahu-summary__ahu" rowSpan={rowspan}>
              {ahu}
            </td>
          )}
          <td className="ahu-summary__col-name">{item.section}</td>
          <td className="ahu-summary__col-right ahu-summary__col-rough ahu-summary__col-fixed-lg">
            {formatRough(item.width, item.height)}
          </td>
          <td className="ahu-summary__col-fixed-md">{item.mounting_plane}</td>
          <td className="ahu-summary__col-fixed">{item.swing}</td>
          <td className="ahu-summary__col-fixed">{item.hinge_side}</td>
        </tr>
      ))}
    </AhuSummaryPane>
  );
};

export default AhuAccessoriesTab;
